import { ApiServiceImpl } from "../core/api.service.impl";
import { ChatMessage, } from "../../models/chat-messages/chat-message.model";
import { ChatMessageList } from "../../models/chat-message-list.model";
import { StreamChat } from "stream-chat";
import { stores } from "../../stores/";
import { accountIdToUserId } from "../../utils";
export class ChatServiceImpl extends ApiServiceImpl {
    constructor(apiKey, encrypterService, signerService) {
        super();
        this.encrypterService = encrypterService;
        this.signerService = signerService;
        this.streamClient = StreamChat.getInstance(apiKey, {
            enableInsights: true,
            enableWSFallback: true,
        });
    }
    async authenticate() {
        try {
            if (!stores.authStore.authSession) {
                throw new Error("No authSession found");
            }
            const response = await this.post(`${this.urls.worker}/token/${stores.authStore.authSession.accountId.toString()}`, undefined, {
                "x-lit-token": stores.authStore.authSession.accessToken,
            });
            this.userId = response.data["userId"];
            this.userToken = response.data["token"];
            await this.streamClient.connectUser({
                id: this.userId,
            }, this.userToken);
            await this.encrypterService.authenticate();
            return this.success(stores.authStore.authSession.accountId);
        }
        catch (e) {
            return this.error(e);
        }
    }
    async send(body, channelId) {
        try {
            const payload = {
                id: crypto.randomUUID(),
                from: stores.authStore.authSession.accountId.toString(),
                ...body,
            };
            const signedMessageResult = await this.signerService.signMessage(payload);
            if (!signedMessageResult.data) {
                throw signedMessageResult.error;
            }
            const streamMessage = await this.sendRaw(signedMessageResult.data, channelId);
            if (streamMessage.data) {
                return this.success(streamMessage.data);
            }
            else {
                throw streamMessage.error;
            }
        }
        catch (e) {
            return this.error(e);
        }
    }
    async sendRaw(payload, channelId) {
        try {
            const channel = this.channelFromId(channelId);
            const encryptedMessageResult = await this.encrypterService.encryptMessage(channel, "", payload);
            if (!encryptedMessageResult.data) {
                throw encryptedMessageResult.error;
            }
            const streamMessage = {
                hashchatMessage: encryptedMessageResult.data,
            };
            const response = await channel.sendMessage({
                id: payload.id,
                ...streamMessage,
            });
            return this.success(response.message);
        }
        catch (e) {
            return this.error(e);
        }
    }
    async openMessage(message, channelId) {
        try {
            const channel = this.channelFromId(channelId);
            const decryptedMessageResult = await this.encrypterService.decryptMessage(channel, message.hashchatMessage);
            if (!decryptedMessageResult.data) {
                throw decryptedMessageResult.error;
            }
            const decryptedMessage = decryptedMessageResult.data;
            decryptedMessage.id = message.id;
            const verifiedMessageResult = await this.signerService.verifyMessage(decryptedMessage);
            if (!verifiedMessageResult.data) {
                throw verifiedMessageResult.error;
            }
            const chatMessage = new ChatMessage({
                ...verifiedMessageResult.data,
                ...decryptedMessage,
            });
            return this.success(chatMessage);
        }
        catch (e) {
            return this.error(e);
        }
    }
    async getMessages(channelId) {
        try {
            // TODO: Add paging support
            const channel = this.channelFromId(channelId);
            const result = await channel.query({
                messages: { limit: 100 },
            });
            const decryptedMessages = await Promise.all(result.messages.map(async (message) => {
                const decryptedMessageResult = await this.encrypterService.decryptMessage(channel, message.hashchatMessage);
                if (!decryptedMessageResult.data) {
                    console.error(decryptedMessageResult.error);
                    return;
                }
                const decryptedMessage = decryptedMessageResult.data;
                decryptedMessage.id = message.id;
                const verifiedMessageResult = await this.signerService.verifyMessage(decryptedMessage);
                if (!verifiedMessageResult.data) {
                    console.error(verifiedMessageResult.error);
                    return;
                }
                return verifiedMessageResult.data;
            }));
            const filteredMessages = decryptedMessages
                .filter((chatMessage) => chatMessage)
                .map((chatMessage) => chatMessage);
            const pendingMessageRequests = new ChatMessageList(filteredMessages);
            return this.success(pendingMessageRequests.items);
        }
        catch (e) {
            return this.error(e);
        }
    }
    async lastMessage(channelId) {
        try {
            const channel = this.channelFromId(channelId);
            await channel.watch();
            const channelLastMessage = channel.lastMessage();
            if (!channelLastMessage) {
                return this.success(undefined);
            }
            const lastMessage = {
                id: channelLastMessage.id,
                hashchatMessage: channelLastMessage.hashchatMessage,
            };
            return this.openMessage(lastMessage, channelId);
        }
        catch (e) {
            return this.error(e);
        }
    }
    async joinChannel(channelId, channelName) {
        try {
            const channel = this.streamClient.channel("team", channelId, {
                name: channelName,
            });
            try {
                await channel.create();
            }
            catch (e) { }
            await channel.addMembers([this.streamClient.userID]);
            return this.success(true);
        }
        catch (e) {
            return this.error(e);
        }
    }
    async createDM(members) {
        try {
            const channel = this.streamClient.channel("messaging", {
                members: members.map(accountIdToUserId),
            });
            try {
                await channel.create();
            }
            catch (e) { }
            return this.success(channel.id);
        }
        catch (e) {
            return this.error(e);
        }
    }
    channelFromId(channelId) {
        const parts = channelId.split(":");
        return this.streamClient.channel(parts[0], parts[1]);
    }
}
